import axios, { type AxiosInstance, type CancelTokenSource } from 'axios';
import { BundledSettingsProvider } from '../../configuration/bundledSettingsProvider';
import type { SiteKey } from '@tamtamy/paperbits-common/src/multisite/siteContract';
import type { SearchResponse, SuggestOption, SearchContent } from './searchTypes';
import type {
    CmsContentContract,
    CmsMetaContract,
    CmsMetaKey,
    CmsPaperbitsPageRefContract
} from '../../cms/content/ko/CmsContentContract';


interface AutocompleteQuery {
    query: string;
    site: SiteKey;
}

interface AutocompleteOptions {
    cancelTokenSource?: CancelTokenSource;
}

export interface SearchQuery {
    query: string;
    site: SiteKey;
    services?: string[];
    industries?: string[];
    category?: string[];
    page?: number;
}

export class SearchClient {

    private readonly axiosInstance: AxiosInstance;

    public constructor() {
        const settingsProvider = BundledSettingsProvider.newInstance();
        const endpoint = settingsProvider.getSettingSync<string>('search/endpoint');
        this.axiosInstance = axios.create({
            baseURL: endpoint
        });
    }

    public async autocomplete(query: AutocompleteQuery, { cancelTokenSource }: AutocompleteOptions = {}): Promise<SuggestOption[]> {
        const { data } = await this.axiosInstance.get<SearchResponse<never>>('/content-autocomplete', {
            params: query,
            cancelToken: cancelTokenSource?.token
        });
        return data.suggest?.content[0].options;
    }

    public async search(query: SearchQuery): Promise<any> {
        const { data } = await this.axiosInstance.get<SearchResponse<SearchContent>>('/content', {
            params: {
                query: query.query,
                site: query.site,
                from: query.page > 0 ? query.page * 10 : 0,
                orderField: 'score',
                services: query.services?.join(','),
                industries: query.industries?.join(','),
                category: query.category?.join(','),
            }
        });
        return data;
        // return data.hits.hits.map(h => searchContentToCmsContentContract(h._source));
    }

    public searchContentToCmsContentContract(sc: SearchContent): CmsContentContract {
        const meta: CmsMetaContract[] = [];
        addMeta(sc, meta, 'content_type');
        addMeta(sc, meta, 'cta_label');
        addMeta(sc, meta, 'cta_type');
        addMeta(sc, meta, 'cta_url');
        addMeta(sc, meta, 'cover_media');
        addMeta(sc, meta, 'document');
        addMeta(sc, meta, 'event_type');
        addMeta(sc, meta, 'event_location');
        addMeta(sc, meta, 'industries');
        addMeta(sc, meta, 'services');
        addMeta(sc, meta, 'card_video');

        const pageRef: CmsPaperbitsPageRefContract = sc.pageRefKey && sc.pageRefPermalink ? {
            key: sc.pageRefKey,
            permalink: sc.pageRefPermalink,
            siteGroup: 'com' // TODO FIXME
        } : null;
        const publicationDate = sc.publicationDate ? {
            time: new Date(sc.publicationDate).getTime()
        } : null;
        return {
            id: sc.id,
            closingDate: sc.closingDate,
            eventDate: sc.eventDate,
            eventTimezone: sc.eventTimezone,
            language: {
                code: sc.languageCode
            },
            meta,
            paperbitsPageRef: pageRef,
            permalink: sc.permalink,
            publicationDate: sc.publicationDate,
            publicationDateObj: publicationDate,
            sectionPermalink: sc.sectionPermalink,
            shortDescription: sc.shortDescription,
            title: sc.title,
            type: sc.type,
            url: sc.url,
            tags: [],
            canonical: null,
            groups: []
        };
    }
}

function addMeta(sc: SearchContent, meta: CmsMetaContract[], key: CmsMetaKey) {
    if (sc[`${key}_ref`]) {
        meta.push({
            key,
            value: null,
            optionValues: sc[`${key}_ref`].map(r => ({
                id: 0,
                order: 0,
                optionValue: {
                    referenceId: r,
                    value: null
                }
            }))
        });
    } else if (sc[key]) {
        meta.push({
            key,
            value: sc[key],
            optionValues: []
        });
    }
}